import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  addCollectionCash,
  getCollectionCashById,
  updateCollectionCash,
} from "../../api/collectionCashApi";
import driverService from "../../api/services/driverService";
import { toast } from "react-toastify";
import SafeImage from "../../components/common/SafeImage";

/**
 * AddCollectionCash Component
 * Form for creating driver collection deposits (bank deposits from drivers)
 * Handles form validation and saves to local storage
 */
const AddCollectionCash = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  // Form state for collection deposit data
  const [formData, setFormData] = useState({
    driverName: "",
    depositAmount: "",
    bankName: "",
    receiptNumber: "",
    receiptImage: null,
    depositDate: isEdit ? "" : new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driversLoading, setDriversLoading] = useState(true);
  const [previewImage, setPreviewImage] = useState(null);

  // Fetch drivers from database
  useEffect(() => {
    const fetchDrivers = async () => {
      try {
        setDriversLoading(true);
        const response = await driverService.getAllDrivers();
        if (response.success) {
          setDrivers(response.data);
        } else {
          toast.error(response.message || "Failed to fetch drivers");
        }
      } catch (error) {
        toast.error("Failed to fetch drivers from database");
        console.error("Error fetching drivers:", error);
      } finally {
        setDriversLoading(false);
      }
    };

    fetchDrivers();
  }, []);

  useEffect(() => {
    if (isEdit) {
      setLoading(true);
      getCollectionCashById(id)
        .then((data) => {
          setFormData({
            driverName: data.driver_name || "",
            depositAmount: data.deposit_amount || "",
            bankName: data.bank_name || "",
            receiptNumber: data.receipt_number || "",
            receiptImage: null,
            depositDate: data.deposit_date || "",
            notes: data.notes || "",
          });
          if (data.receipt_image) {
            setPreviewImage(data.receipt_image);
          }
        })
        .catch(() => toast.error("Failed to load collection cash"))
        .finally(() => setLoading(false));
    } else {
      setFormData((prev) => ({
        ...prev,
        depositDate: new Date().toISOString().slice(0, 10),
      }));
    }
  }, [isEdit, id]);

  // Handle form input changes and update state
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prevState) => ({
        ...prevState,
        receiptImage: file,
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Submit form data to save collection deposit to local storage
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const submitData = {
        driver_name: formData.driverName,
        deposit_amount: formData.depositAmount,
        bank_name: formData.bankName,
        receipt_number: formData.receiptNumber,
        deposit_date: formData.depositDate,
        notes: formData.notes,
      };

      // Add receipt image if selected
      if (formData.receiptImage) {
        submitData.receipt_image = formData.receiptImage;
      }

      if (isEdit) {
        await updateCollectionCash(id, submitData);
        toast.success("Collection cash updated successfully!");
      } else {
        await addCollectionCash(submitData);
        toast.success("Collection cash added successfully!");
      }
      navigate("/collectioncash");
    } catch (error) {
      toast.error("Failed to save collection cash. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-5">Loading...</div>;

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header">
          <h5>{isEdit ? "Edit" : "Add"} Collection Cash</h5>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="row g-3">
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Driver Name</label>
                  <select
                    className="form-select"
                    name="driverName"
                    value={formData.driverName}
                    onChange={handleChange}
                    required
                    disabled={driversLoading}
                  >
                    <option value="" disabled>
                      {driversLoading
                        ? "Loading drivers..."
                        : "Select a driver"}
                    </option>
                    {drivers.map((driver) => (
                      <option
                        key={driver.delivery_boy_id}
                        value={driver.user.name}
                      >
                        {driver.user.name} - {driver.vehicle_no} (
                        {driver.vehicle_type})
                      </option>
                    ))}
                  </select>
                  {driversLoading && (
                    <small className="text-muted">
                      Loading drivers from database...
                    </small>
                  )}
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Deposit Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    name="depositAmount"
                    value={formData.depositAmount}
                    onChange={handleChange}
                    placeholder="Enter amount"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <input
                    type="text"
                    className="form-control"
                    name="bankName"
                    value={formData.bankName}
                    onChange={handleChange}
                    placeholder="Enter bank name"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Receipt Number</label>
                  <input
                    type="text"
                    className="form-control"
                    name="receiptNumber"
                    value={formData.receiptNumber}
                    onChange={handleChange}
                    placeholder="Enter receipt number"
                    required
                  />
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Receipt Image</label>
                  <input
                    type="file"
                    className="form-control"
                    name="receiptImage"
                    onChange={handleFileChange}
                    accept="image/*"
                  />
                  <small className="text-muted">
                    Accepted formats: JPEG, PNG, JPG, GIF (Max: 2MB)
                  </small>
                </div>
              </div>
              <div className="col-md-6">
                <div className="form-group">
                  <label className="form-label">Deposit Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="depositDate"
                    value={formData.depositDate}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    className="form-control"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    placeholder="Enter any additional notes"
                    rows="3"
                  />
                </div>
              </div>
              {previewImage && (
                <div className="col-12">
                  <div className="form-group">
                    <label className="form-label">Receipt Preview</label>
                    <div className="mt-2">
                      <SafeImage
                        src={previewImage}
                        alt="Receipt preview"
                        className="img-thumbnail"
                        style={{ maxWidth: "300px", maxHeight: "300px" }}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="col-12">
                <div className="d-flex gap-3 justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-secondary px-4"
                    onClick={() => navigate("/collectioncash")}
                    disabled={submitting}
                    style={{
                      width: "120px",
                      height: "38px",
                      fontSize: "15px",
                      borderRadius: "5px",
                      fontWeight: 500,
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                    disabled={submitting || driversLoading}
                    style={{
                      width: "120px",
                      height: "38px",
                      fontSize: "15px",
                      borderRadius: "5px",
                      fontWeight: 500,
                    }}
                  >
                    {submitting
                      ? isEdit
                        ? "Updating..."
                        : "Saving..."
                      : isEdit
                        ? "Update"
                        : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddCollectionCash;
